import { useState, useEffect } from "react";
import { BsClock } from "react-icons/bs";
import { toast } from "react-toastify";
import api from "../../../api";

export default function MarkAttendance() {
  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  const [attendance, setAttendance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => setCurrentDateTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    fetchTodaysAttendance();
  }, []);

  const fetchTodaysAttendance = async () => {
    try {
      const { data } = await api.get(`${process.env.REACT_APP_API_BASE_URL}attendance`);
      setAttendance(data);
    } catch (error) {
      toast.error("Error fetching today's attendance");
      console.error("Fetch Attendance Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const getLocationAndMarkAttendance = async (type) => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser.");
      return;
    }

    setIsLoading(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        try {
          const { data } = await api.post(
            `${process.env.REACT_APP_API_BASE_URL}attendance/${type}`,
            { latitude, longitude }
          );
          toast.success(data.message);
          fetchTodaysAttendance();
        } catch (error) {
          toast.error(error.response?.data?.message || `Error during ${type}`);
          console.error("API Error:", error);
        } finally {
          setIsLoading(false);
        }
      },
      (error) => {
        console.error("Geolocation Error:", error);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            toast.error("Location permission denied. Please enable it in your browser settings.");
            break;
          case error.POSITION_UNAVAILABLE:
            toast.error("Location information is unavailable.");
            break;
          case error.TIMEOUT:
            toast.error("Location request timed out. Try again.");
            break;
          default:
            toast.error("An unknown location error occurred.");
        }
        setIsLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  };

  const calculateDuration = (start, end) => {
    const startTime = new Date(start);
    const endTime = new Date(end);
    const diffMs = endTime - startTime;
    const hours = Math.floor(diffMs / 3600000);
    const minutes = Math.floor((diffMs % 3600000) / 60000);
    return `${hours}h ${minutes}m`;
  };

  const formatTime = (utcTime) => {
    return new Date(utcTime).toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  };

  return (
    <div className="grid gap-6 md:grid-cols-2">
      <div className="p-6 border border-gray-300 rounded-lg">
        <h2 className="text-xl font-semibold mb-2">Attendance Check-in/out</h2>
        <p className="text-gray-600 mb-4">Record your daily attendance</p>
        <div className="text-center mb-6">
          <h3 className="text-4xl font-bold">
            {currentDateTime.toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata" })}
          </h3>
          <p className="text-lg mt-1">
            {currentDateTime.toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" })}
          </p>
        </div>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <button
            onClick={() => getLocationAndMarkAttendance("clock-in")}
            disabled={!!attendance?.clockInTime}
            className="bg-blue-500 text-white py-2 rounded-lg disabled:bg-gray-300"
          >
            Check In
          </button>
          <button
            onClick={() => getLocationAndMarkAttendance("clock-out")}
            disabled={!attendance?.clockInTime || !!attendance?.clockOutTime}
            className="bg-gray-500 text-white py-2 rounded-lg disabled:bg-gray-300"
          >
            Check Out
          </button>
        </div>
        {isLoading && <p className="text-sm text-gray-600">Getting location...</p>}
        {attendance?.clockInTime && (
          <p className="text-sm">Checked in at: {formatTime(attendance.clockInTime)}</p>
        )}
        {attendance?.clockOutTime && (
          <p className="text-sm">Checked out at: {formatTime(attendance.clockOutTime)}</p>
        )}
        {attendance?.clockInTime && attendance?.clockOutTime && (
          <p className="text-sm text-green-600">Today's attendance is complete</p>
        )}
      </div>

      <div className="p-6 border border-gray-300 rounded-lg">
        <h2 className="text-xl font-semibold mb-2">Recent Activity</h2>
        {attendance ? (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-100">
                  <th className="p-2 border">Activity</th>
                  <th className="p-2 border">Time</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t">
                  <td className="p-2 border flex items-center">
                    <BsClock className="mr-2" /> Checked In
                  </td>
                  <td className="p-2 border text-gray-600">
                    {formatTime(attendance.clockInTime)}
                  </td>
                </tr>
                {attendance.clockOutTime && (
                  <tr className="border-t">
                    <td className="p-2 border flex items-center">
                      <BsClock className="mr-2" /> Checked Out
                    </td>
                    <td className="p-2 border text-gray-600">
                      {formatTime(attendance.clockOutTime)}
                    </td>
                  </tr>
                )}
                {attendance.clockInTime && attendance.clockOutTime && (
                  <tr className="border-t bg-green-50 font-medium">
                    <td className="p-2 border">Total Hours</td>
                    <td className="p-2 border text-green-700">
                      {calculateDuration(attendance.clockInTime, attendance.clockOutTime)}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-center py-6 text-gray-600">No recent activity</p>
        )}
      </div>
    </div>
  );
}
